import React, { useState, useEffect, useCallback } from 'react';
import { BellRing, Save, Loader, AlertCircle, Plus, X, Play, Check } from 'lucide-react';
import { orderAlerts } from '../services/api';

// Order Alerts (Matt, board 2020): when an alert about an order (Access Point over
// 44 lb, 2 boxes, missing apartment number, fraud, address checks) is not handled,
// CPx sends a STILL OPEN follow-up and a LAST CALL before the UPS pickup. The times
// live here so they can change for Saturdays and holidays. Server: cp-alerts.js.

const INPUT = { padding: '7px 10px', border: '1px solid var(--gray-300)', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' };
const LABEL = { fontSize: 11, fontWeight: 600, color: 'var(--gray-600)', marginBottom: 4, display: 'block' };
const SECTION = { background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 8, padding: 16, marginBottom: 16 };
const TITLE = { fontSize: 13, fontWeight: 700, color: 'var(--navy)', marginBottom: 12 };
const GRID = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 };
const DOWS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MODES = [
  { v: 'off', l: 'Off', d: 'Nothing is registered or chased.' },
  { v: 'test', l: 'Test', d: 'Alerts carry the Mark handled link, but every follow-up, last call and outcome note goes only to the test recipient.' },
  { v: 'live', l: 'Live', d: 'Follow-ups go to everyone on the alert; the last call also goes to the floor.' },
];
const STATUS_COLOR = { open: 'var(--orange)', handled: '#1E7B3C', expired: 'var(--gray-500)' };

function dayText(ymd) {
  return new Date(ymd + 'T12:00:00Z').toLocaleDateString('en-US', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

export default function OrderAlertsSettings() {
  const [s, setS] = useState(null);
  const [overrides, setOverrides] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [ov, setOv] = useState({ day: '', ship_day: true, pickup_time: '', last_call_time: '', note: '' });
  const [dry, setDry] = useState(null);
  const [dryRunning, setDryRunning] = useState(false);
  const [noteFor, setNoteFor] = useState({});

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [cfg, list] = await Promise.all([orderAlerts('settings'), orderAlerts('list', { days: 5 })]);
      setS(cfg.settings); setOverrides(cfg.overrides || []); setAlerts(list.alerts || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3000); };
  const set = (k, v) => setS({ ...s, [k]: v });

  const save = async () => {
    setSaving(true); setError('');
    try { const r = await orderAlerts('save-settings', { settings: s }); setS(r.settings); flash('Saved.'); }
    catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };
  const saveOverride = async () => {
    setError('');
    try { await orderAlerts('save-override', ov); setOv({ day: '', ship_day: true, pickup_time: '', last_call_time: '', note: '' }); flash('Date saved.'); load(); }
    catch (e) { setError(e.message); }
  };
  const delOverride = async (day) => {
    try { await orderAlerts('delete-override', { day }); load(); } catch (e) { setError(e.message); }
  };
  const runDry = async () => {
    setDryRunning(true); setError('');
    try { setDry(await orderAlerts('sweep', { dryRun: true })); } catch (e) { setError(e.message); }
    finally { setDryRunning(false); }
  };
  const markHandled = async (id) => {
    const note = (noteFor[id] || '').trim();
    if (!note) { setError('Add a one-line note first.'); return; }
    try { await orderAlerts('mark-handled', { id, note }); setNoteFor({ ...noteFor, [id]: '' }); flash('Marked handled.'); load(); }
    catch (e) { setError(e.message); }
  };
  // Kept as typed; the server splits on commas and spaces when it saves.
  const listEdit = (k) => (e) => set(k, e.target.value);
  const listVal = (v) => (Array.isArray(v) ? v.join(', ') : (v || ''));

  if (loading && !s) return <div className="page-content" style={{ padding: 24 }}><Loader size={16} className="spin" /> Loading...</div>;

  return (
    <div className="page-content" style={{ padding: 24, maxWidth: 1100 }}>
      <h1 style={{ fontSize: 20, color: 'var(--navy)', display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 4px' }}>
        <BellRing size={20} /> Order Alerts
      </h1>
      <p style={{ fontSize: 13, color: 'var(--gray-600)', margin: '0 0 16px', maxWidth: 760 }}>
        An alert about an order counts as handled when someone reply-alls with what is being done, or clicks Mark handled in the
        email and leaves a one-line note. If nobody does, CPx sends a STILL OPEN follow-up, then a last call before the UPS pickup on
        ship day that also goes to the floor. When it is handled, everyone on the alert gets a short note saying who did what.
      </p>
      {error && <div style={{ color: 'var(--red, #B42318)', fontSize: 13, marginBottom: 12, display: 'flex', gap: 6 }}><AlertCircle size={15} /> {error}</div>}
      {msg && <div style={{ color: '#1E7B3C', fontSize: 13, marginBottom: 12 }}>{msg}</div>}

      {s && (
        <div style={SECTION}>
          <div style={TITLE}>Mode</div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {MODES.map((m) => (
              <label key={m.v} style={{ ...INPUT, cursor: 'pointer', flex: '1 1 220px', borderColor: s.mode === m.v ? 'var(--orange)' : 'var(--gray-300)' }}>
                <input type="radio" name="mode" checked={s.mode === m.v} onChange={() => set('mode', m.v)} /> <b>{m.l}</b>
                <div style={{ fontSize: 11.5, color: 'var(--gray-600)', marginTop: 4 }}>{m.d}</div>
              </label>
            ))}
          </div>
        </div>
      )}

      {s && (
        <div style={SECTION}>
          <div style={TITLE}>Times (Eastern)</div>
          <div style={GRID}>
            <div><label style={LABEL}>UPS pickup</label><input type="time" style={INPUT} value={s.pickup_time} onChange={(e) => set('pickup_time', e.target.value)} /></div>
            <div><label style={LABEL}>Last call on ship day</label><input type="time" style={INPUT} value={s.last_call_time} onChange={(e) => set('last_call_time', e.target.value)} /></div>
            <div><label style={LABEL}>STILL OPEN after (minutes)</label><input type="number" min="15" max="600" style={INPUT} value={s.followup_delay_min} onChange={(e) => set('followup_delay_min', e.target.value)} /></div>
            <div><label style={LABEL}>Work day starts</label><input type="time" style={INPUT} value={s.work_start} onChange={(e) => set('work_start', e.target.value)} /></div>
            <div><label style={LABEL}>Work day ends</label><input type="time" style={INPUT} value={s.work_end} onChange={(e) => set('work_end', e.target.value)} /></div>
          </div>
          <div style={{ marginTop: 12 }}>
            <label style={LABEL}>Days nobody ships (a follow-up due then waits for the next work morning)</label>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {DOWS.map((d, i) => (
                <label key={d} style={{ fontSize: 13 }}>
                  <input type="checkbox" checked={(s.no_ship_dows || []).includes(i)}
                    onChange={(e) => set('no_ship_dows', e.target.checked ? [...(s.no_ship_dows || []), i] : (s.no_ship_dows || []).filter((x) => x !== i))} /> {d}
                </label>
              ))}
            </div>
          </div>
          <div style={{ ...GRID, marginTop: 12 }}>
            <div><label style={LABEL}>Floor contacts (get the last call)</label><input style={{ ...INPUT, width: '100%' }} value={listVal(s.floor_contacts)} onChange={listEdit('floor_contacts')} /></div>
            <div><label style={LABEL}>Watch mailbox (where connector alerts and replies are read)</label><input style={{ ...INPUT, width: '100%' }} value={listVal(s.watch_mailboxes)} onChange={listEdit('watch_mailboxes')} /></div>
            <div><label style={LABEL}>Test recipient</label><input style={{ ...INPUT, width: '100%' }} value={s.test_recipient || ''} onChange={(e) => set('test_recipient', e.target.value)} /></div>
          </div>
          <button className="btn btn-primary" style={{ marginTop: 14 }} onClick={save} disabled={saving}>
            {saving ? <Loader size={14} className="spin" /> : <Save size={14} />} Save
          </button>
        </div>
      )}

      <div style={SECTION}>
        <div style={TITLE}>Saturdays and holidays</div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div><label style={LABEL}>Date</label><input type="date" style={INPUT} value={ov.day} onChange={(e) => setOv({ ...ov, day: e.target.value })} /></div>
          <div><label style={LABEL}>Shipping that day?</label>
            <select style={INPUT} value={ov.ship_day ? 'yes' : 'no'} onChange={(e) => setOv({ ...ov, ship_day: e.target.value === 'yes' })}>
              <option value="yes">Yes, we ship</option><option value="no">No, closed</option>
            </select></div>
          <div><label style={LABEL}>Pickup</label><input type="time" style={INPUT} value={ov.pickup_time} onChange={(e) => setOv({ ...ov, pickup_time: e.target.value })} /></div>
          <div><label style={LABEL}>Last call</label><input type="time" style={INPUT} value={ov.last_call_time} onChange={(e) => setOv({ ...ov, last_call_time: e.target.value })} /></div>
          <div style={{ flex: '1 1 180px' }}><label style={LABEL}>Note</label><input style={{ ...INPUT, width: '100%' }} value={ov.note} placeholder="Saturday before Christmas" onChange={(e) => setOv({ ...ov, note: e.target.value })} /></div>
          <button className="btn btn-navy" onClick={saveOverride} disabled={!ov.day}><Plus size={14} /> Add date</button>
        </div>
        {overrides.length > 0 && (
          <table className="product-table" style={{ marginTop: 12 }}>
            <thead><tr><th>Date</th><th>Ships</th><th>Pickup</th><th>Last call</th><th>Note</th><th /></tr></thead>
            <tbody>{overrides.map((o) => (
              <tr key={o.day}><td>{dayText(o.day)}</td><td>{o.ship_day ? 'Yes' : 'Closed'}</td>
                <td>{o.pickup_time || 'default'}</td><td>{o.last_call_time || 'default'}</td><td>{o.note}</td>
                <td><button className="btn btn-ghost" onClick={() => delOverride(o.day)} title="Remove"><X size={14} /></button></td></tr>
            ))}</tbody>
          </table>
        )}
      </div>

      <div style={SECTION}>
        <div style={{ ...TITLE, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Recent alerts</span>
          <button className="btn btn-outline" onClick={runDry} disabled={dryRunning}>
            {dryRunning ? <Loader size={14} className="spin" /> : <Play size={14} />} What would the next run send?
          </button>
        </div>
        {dry && (
          <div style={{ fontSize: 12.5, background: 'var(--gray-50, #F7F9FA)', border: '1px solid var(--gray-200)', borderRadius: 6, padding: 10, marginBottom: 12 }}>
            <div><b>Mode:</b> {dry.mode}. Read {dry.mailRead || 0} messages. {dry.notes && dry.notes.length ? dry.notes.join(' ') : ''}</div>
            {(dry.registered || []).map((r, i) => <div key={'r' + i}>Would register: {r.subject} (ships {r.shipDate || 'not set'})</div>)}
            {(dry.handled || []).map((h, i) => <div key={'h' + i}>Would mark handled: order {h.order}, {h.wouldMarkHandled ? `${h.wouldMarkHandled.by}: ${h.wouldMarkHandled.note}` : ''}</div>)}
            {(dry.sends || []).map((x, i) => <div key={'s' + i}>Would send {x.stage}: {x.subject} to {(x.to || []).join(', ')}{x.liveTo && x.to && x.liveTo.join() !== x.to.join() ? ` (live: ${x.liveTo.join(', ')})` : ''}</div>)}
            {!(dry.registered || []).length && !(dry.sends || []).length && !(dry.handled || []).length && <div>Nothing due.</div>}
          </div>
        )}
        {alerts.length === 0 ? <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>No alerts in the last 5 days.</div> : (
          <table className="product-table">
            <thead><tr><th>Alert</th><th>Sent</th><th>Ships</th><th>Follow-up</th><th>Last call</th><th>Status</th></tr></thead>
            <tbody>{alerts.map((a) => (
              <tr key={a.id}>
                <td style={{ maxWidth: 340 }}><div style={{ fontWeight: 600 }}>{a.subject}</div>
                  <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>A-{a.id} {a.kindLabel}{a.hold === 'label' ? ', label held' : ''}{a.origin === 'connector' ? ', from the BC connector' : ''}</div>
                  {a.lastError && <div style={{ fontSize: 11, color: '#B42318' }}>{a.lastError}</div>}</td>
                <td>{a.sent}</td><td>{a.ships || ''}</td><td>{a.followup || ''}</td><td>{a.lastCall || ''}</td>
                <td>
                  <span style={{ color: STATUS_COLOR[a.status] || 'inherit', fontWeight: 700, textTransform: 'capitalize' }}>{a.status}</span>
                  {a.handled && <div style={{ fontSize: 11.5 }}>{a.handled}</div>}
                  {a.status === 'open' && (
                    <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                      <input style={{ ...INPUT, padding: '4px 6px', fontSize: 12, width: 150 }} placeholder="What was done" value={noteFor[a.id] || ''}
                        onChange={(e) => setNoteFor({ ...noteFor, [a.id]: e.target.value })} />
                      <button className="btn btn-ghost" title="Mark handled" onClick={() => markHandled(a.id)}><Check size={14} /></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}
